import { AxiosResponse } from "axios";
import HttpService from "src/services/httpService";
import RouteService from "src/services/routeService";
import { ApiResponse } from "src/types";

import { unpackError } from "@dashboard/devices/api/helpers";

const jsonHeaders = { "content-type": "application/json" };

export type LicenseStatistics = {
    totalLicenses: number;
    totalAssociatedLicenses: number;
    totalRevokedLicenses: number;
};

export type LicenseInspection = {
    serverData: string;
    serverNonce: string;
    deviceData: string;
    deviceNonce: string;
};

const byKey = (searchTerm: string, suffix = "") => `${encodeURIComponent(searchTerm)}${suffix}`;

export async function fetchLicenseStatistics(signal?: AbortSignal) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<LicenseStatistics>>>(`${endpoint}statistics`, {
            signal,
        });
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
}

type GrantLicensesPayload = {
    userEmail: string;
    groupId: number;
    grantCount: number;
};
export async function grantLicenses({ userEmail, groupId, grantCount }: GrantLicensesPayload) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.post(endpoint, { email: userEmail, groupId, grantCount }, { headers: jsonHeaders });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function moveLicense({ licenseKey, groupId }: { licenseKey: string; groupId: number }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.patch(`${endpoint}${byKey(licenseKey, "/group")}`, { groupId }, { headers: jsonHeaders });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function revokeLicense({ licenseKey, reason }: { licenseKey: string; reason: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.post(`${endpoint}${byKey(licenseKey, "/revocation")}`, { reason }, { headers: jsonHeaders });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function reviveLicense({ licenseKey, groupId, reason }: { licenseKey: string; groupId: number; reason: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.delete(`${endpoint}${byKey(licenseKey, "/revocation")}`, {
            params: { groupId, reason },
        });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function attachLicense({ licenseKey, deviceSerial }: { licenseKey: string; deviceSerial: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.post(`${endpoint}${byKey(licenseKey, "/device")}`, { deviceSerial }, { headers: jsonHeaders });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function detachLicense({ licenseKey, reason }: { licenseKey: string; reason: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.delete(`${endpoint}${byKey(licenseKey, "/device")}`, { params: { reason } });
    } catch (error) {
        throw unpackError(error);
    }
}

export async function inspectLicense({ licenseKey }: { licenseKey: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        const { data } = await HttpService.get<string, AxiosResponse<ApiResponse<LicenseInspection>>>(
            `${endpoint}${byKey(licenseKey, "/inspection")}`,
        );
        return data.model;
    } catch (error) {
        throw unpackError(error);
    }
}

export async function deleteLicenses({ licenseKey, reason }: { licenseKey: string; reason: string }) {
    try {
        const endpoint = await RouteService.getAdminLicensesRoute();
        await HttpService.delete(`${endpoint}${byKey(licenseKey)}`, { params: { reason } });
    } catch (error) {
        throw unpackError(error);
    }
}
